import React, { useRef, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';

export interface Certif {
    cert_reg_number: string;
    comment_apts: string;
    cert_link: string;
    cert_schema: string;
    cert_status: string;
    cert_reg_data: string;
    cert_finish_data: string;
    cert_stop_data: string | null;
    cert_stop_reason: string;
    applicant_inn: string;
    applicant_type: string;
    applicant_ogrn: string;
    applicant_fullname: string;
    applicant_shortname: string;
    applicant_adres: string;
    manufacture_inn: string;
    manufacture_fullname: string;
    manufacture_adres: string;
    manufacture_ogrn: string;
    manufacture_workplace: string;
    product_fullname: string;
    technical_docs: string;
    regulatory_documentation: string;
    lab_number_1: string;
    lan_name_1: string;
    lab_number_2: string;
    lan_name_2: string;
    lab_number_3: string;
    lan_name_3: string;
    os_number: string;
    os_name: string;
    os_ogrn: string;
    os_status: string;
    os_stop_data: string;
    smk_cert_number: string;
    smk_os_number: string;
    smk_os_name: string;
    cert_docs: string;
    cert_violation: string;
    lab_violation: string;
    os_violation: string;
    smk_violation: string;
    full_comment_apts: string;
    signature_person: string;
    product_import: string;
    custom_doc_type: string;
    number_custom_doc: string;
    custom_rejection: string;
    last_data_protocol: string;
    exam_manufacture_start_data: string;
    exam_manufacture_stop_data: string;
    sampling_data: string | null;
    decision_data: string;
    taken_simpling_data: string | null;
    taken_place: string;
    exam_expert: string;
    all_expert: string;
    date_update: string;
    applicant_region: string;
    region_manufacturer: string;
}

interface Filters {
    cert_reg_number: string[];
    os_name: string[];
    os_number: string[];
    applicant_shortname: string[];
    manufacture_inn: string[];
    cert_reg_data: string[];
    manufacture_fullname: string[];
    applicant_inn: string[];
    applicant_region: string[];
    region_manufacturer: string[];
}

interface BarChartDynamicsProps {
    certif: Certif[];
    filteredAptsComments: string[];
    filteredCertStatuses: string[];
    filters: Filters;
    filteredYears?: string[];
    onBarClick?: (year: string) => void;
}

const nivoKeys = ['Нарушений нет', 'Есть нарушения', 'Обратить внимание'];
const nivoColors = [
    'rgba(126, 178, 70, 1)',
    'rgba(237, 93, 98, 1)',
    'rgba(255, 166, 0, 1)'
];

const BarChartDynamics: React.FC<BarChartDynamicsProps> = ({ certif, filteredAptsComments, filteredCertStatuses, filters, filteredYears, onBarClick }) => {
    const getDynamicsData = () => {
        const years = ['2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'];
        const filteredCertificates = certif.filter((cert: Certif) => {
            const matchesAptsComment = filteredAptsComments.length === 0 || 
                filteredAptsComments.some((comment: string) => 
                    cert.comment_apts?.toLowerCase() === comment.toLowerCase()
                );
            const matchesCertStatus = filteredCertStatuses.length === 0 || 
                filteredCertStatuses.some((status: string) => 
                    cert.cert_status?.toLowerCase() === status.toLowerCase()
                );
            const matchesFilters = Object.entries(filters).every(([key, values]) => {
                if ((values as string[]).length === 0) return true;
                const certValue = cert[key as keyof Certif]?.toString() || '';
                return (values as string[]).some((value: string) => 
                    certValue.toLowerCase().includes(value.toLowerCase())
                );
            });
            return matchesAptsComment && matchesCertStatus && matchesFilters;
        });
        const dataByYear = years.map((year: string) => {
            const yearCertificates = filteredCertificates.filter((cert: Certif) => {
                const certYear = cert.cert_reg_data ? cert.cert_reg_data.substring(0, 4) : '';
                return certYear === year;
            });
            return {
                year,
                noViolations: yearCertificates.filter((cert: Certif) => cert.comment_apts === "нарушений нет").length,
                hasViolations: yearCertificates.filter((cert: Certif) => cert.comment_apts === "есть нарушения").length,
                attention: yearCertificates.filter((cert: Certif) => cert.comment_apts === "обратить внимание").length
            };
        });
        return {
            labels: years,
            datasets: [
                { label: 'Нарушений нет', data: dataByYear.map(item => item.noViolations) },
                { label: 'Есть нарушения', data: dataByYear.map(item => item.hasViolations) },
                { label: 'Обратить внимание', data: dataByYear.map(item => item.attention) }
            ]
        };
    };
    const getNivoBarData = () => {
        const data = getDynamicsData();
        return data.labels.map((year: string, i: number) => ({
            year: String(year),
            'Нарушений нет': Number(data.datasets[0].data[i]),
            'Есть нарушения': Number(data.datasets[1].data[i]),
            'Обратить внимание': Number(data.datasets[2].data[i]),
        }) as Record<string, any>);
    };
    const [barTooltip, setBarTooltip] = useState<null | { x: number; y: number; bar: any }>(null);
    const barChartRef = useRef<HTMLDivElement>(null);
    const tooltipWidth = 320;
    const tooltipHeight = 160;
    const [hoveredKey, setHoveredKey] = useState<string | null>(null);

    function handleBarChartMouseMove(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        if (!barChartRef.current) return;
        const svg = barChartRef.current.querySelector('svg');
        if (!svg) return;
        const bars = Array.from(svg.querySelectorAll('rect[data-id]'));
        for (const rect of bars) {
            const bbox = rect.getBoundingClientRect();
            if (
                e.clientX >= bbox.left &&
                e.clientX <= bbox.right &&
                e.clientY >= bbox.top &&
                e.clientY <= bbox.bottom
            ) {
                const dataId = rect.getAttribute('data-id');
                const year = rect.parentElement?.parentElement?.querySelector('text')?.textContent || '';
                const barData = getNivoBarData().find((d: any) => d.year === year);
                if (barData) {
                    setBarTooltip({
                        x: e.nativeEvent.offsetX,
                        y: e.nativeEvent.offsetY,
                        bar: {
                            id: dataId,
                            value: barData[dataId || ''] || 0,
                            color: rect.getAttribute('fill'),
                            indexValue: year,
                            data: barData
                        }
                    });
                    return;
                }
            }
        }
        setBarTooltip(null);
    }
    function handleBarChartMouseLeave() {
        setBarTooltip(null);
    }
    const nivoBarData = getNivoBarData();
    const getIntegerTickValues = (maxValue: number): number[] => {
        if (!isFinite(maxValue) || maxValue <= 0) return [0];
        const niceStep = (n: number) => {
            const pow = Math.pow(10, Math.floor(Math.log10(n)));
            const d = n / pow;
            let step: number;
            if (d <= 1) step = 1;
            else if (d <= 2) step = 2;
            else if (d <= 5) step = 5;
            else step = 10;
            return step * pow;
        };
        const roughStep = Math.ceil(maxValue / 8);
        const step = Math.max(1, niceStep(roughStep));
        const upper = Math.ceil(maxValue / step) * step;
        const ticks: number[] = [];
        for (let v = 0; v <= upper; v += step) ticks.push(v);
        return ticks;
    };
    let legendYear = null;
    if (hoveredKey && barTooltip && barTooltip.bar && barTooltip.bar.indexValue) {
        legendYear = barTooltip.bar.indexValue;
    } else if (filteredYears && filteredYears.length === 1) {
        legendYear = filteredYears[0];
    }
    let legendData = null;
    if (legendYear) {
        legendData = nivoBarData.find(d => d.year === legendYear);
    } else {
        legendData = nivoBarData.reduce((acc, d) => {
            nivoKeys.forEach(key => {
                acc[key] = (acc[key] || 0) + (d[key] || 0);
            });
            return acc;
        }, { year: 'Все' });
    }
    if (!legendData) {
        legendData = { year: '', ...Object.fromEntries(nivoKeys.map(k => [k, 0])) };
    }
    const legendTotal = nivoKeys.reduce((sum, key) => sum + (legendData[key] || 0), 0);
    let tooltipX = 0, tooltipY = 0, tooltipContent = null;
    if (barTooltip && barTooltip.bar) {
        const { id, indexValue } = barTooltip.bar;
        const info = nivoBarData.find((d: any) => d.year === String(indexValue));
        if (info) {
            const value = info[String(id)] || 0;
            const total = nivoKeys.reduce((sum, key) => sum + (info[key] || 0), 0);
            const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
            const colorIdx = nivoKeys.indexOf(String(id));
            const color = nivoColors[colorIdx % nivoColors.length];
            tooltipX = barTooltip.x + 18;
            tooltipY = barTooltip.y + 10;
            if (barChartRef.current) {
                const rect = barChartRef.current.getBoundingClientRect();
                if (tooltipX + tooltipWidth > rect.width) tooltipX = barTooltip.x - tooltipWidth - 18;
                if (tooltipX < 0) tooltipX = 0;
                if (tooltipY + tooltipHeight > rect.height) tooltipY = barTooltip.y - tooltipHeight - 10;
                if (tooltipY < 0) tooltipY = 0;
            }
            tooltipContent = (
                <div
                    style={{
                        position: 'absolute',
                        left: tooltipX,
                        top: tooltipY,
                        zIndex: 1000,
                        pointerEvents: 'none',
                        padding: 16,
                        background: '#fff',
                        border: '1.5px solid #eee',
                        borderRadius: 10,
                        minWidth: tooltipWidth,
                        maxWidth: tooltipWidth,
                        boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                        fontSize: 15,
                        color: '#222',
                        fontFamily: 'inherit',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontWeight: 700, color, fontSize: 20 }}>{String(id)}</span>
                        <span style={{ fontSize: 16, color: '#888' }}>Год: <b>{indexValue}</b></span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, marginBottom: 2 }}>
                        <span>Количество:</span>
                        <b style={{ fontWeight: 700, fontSize: 18 }}>{value}</b>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, marginBottom: 8 }}>
                        <span>Процент:</span>
                        <b style={{ color, fontWeight: 700, fontSize: 18 }}>{percent}%</b>
                    </div>
                    <div style={{ borderTop: '1px solid #eee', margin: '8px 0 8px 0' }} />
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Всего за год: {total}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 15 }}>
                        {nivoKeys.map((key, idx) => {
                            const v = info[key] || 0;
                            const p = total > 0 ? ((v / total) * 100).toFixed(1) : '0.0';
                            return (
                                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 1 }}>
                                    <span style={{ color: nivoColors[idx % nivoColors.length], fontWeight: 600, minWidth: 120 }}>{key}</span>
                                    <span style={{ fontWeight: 500 }}>{v} ({p}%)</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
    }
    return (
        <div className="bar-chart" style={{ width: '100%', height: '100%', position: 'relative' }} ref={barChartRef} onMouseMove={handleBarChartMouseMove} onMouseLeave={handleBarChartMouseLeave}>
            <ResponsiveBar
                data={nivoBarData}
                keys={nivoKeys}
                indexBy="year"
                margin={{ top: 50, right: 60, bottom: 80, left: 80 }}
                padding={0.3}
                groupMode="stacked"
                colors={({ id }) => {
                    console.log('id:', id, 'hoveredKey:', hoveredKey);
                    const idx = nivoKeys.indexOf(String(id));
                    if (hoveredKey && hoveredKey !== id) {
                        return nivoColors[idx % nivoColors.length].replace('1)', '0.2)');
                    }
                    return nivoColors[idx % nivoColors.length];
                }}
                borderRadius={4}
                enableLabel={true}
                labelSkipWidth={16}
                labelSkipHeight={16}
                labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Год',
                    legendPosition: 'middle',
                    legendOffset: 40,
                    truncateTickAt: 0
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: 'Количество сертификатов',
                    legendPosition: 'middle',
                    legendOffset: -50,
                    tickValues: (() => {
                        const max = nivoBarData.reduce((m, row) => {
                            const total = nivoKeys.reduce((s, k) => s + (row[k] || 0), 0);
                            return Math.max(m, total);
                        }, 0);
                        return getIntegerTickValues(max);
                    })()
                }}
                tooltip={({ id, value, color, indexValue, data }) => {
                    const info = nivoBarData.find((d: any) => d.year === indexValue);
                    if (!info) return null;
                    const idStr = String(id);
                    const valueNum = info[idStr] || 0;
                    const total = nivoKeys.reduce((sum, key) => sum + (info[key] || 0), 0);
                    const percent = total > 0 ? ((valueNum / total) * 100).toFixed(1) : '0.0';
                    const colorIdx = nivoKeys.indexOf(idStr);
                    const colorVal = nivoColors[colorIdx % nivoColors.length];
                    return (
                        <div
                            style={{
                                padding: 16,
                                background: '#fff',
                                border: '1.5px solid #eee',
                                borderRadius: 10,
                                minWidth: tooltipWidth,
                                maxWidth: tooltipWidth,
                                boxShadow: '0 2px 12px rgba(0,0,0,0.10)',
                                fontSize: 15,
                                color: '#222',
                                fontFamily: 'inherit',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <span style={{ fontWeight: 700, color: colorVal, fontSize: 20 }}>{idStr}</span>
                                <span style={{ fontSize: 16, color: '#888' }}>Год: <b>{indexValue}</b></span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, marginBottom: 2 }}>
                                <span>Количество:</span>
                                <b style={{ fontWeight: 700, fontSize: 18 }}>{valueNum}</b>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, marginBottom: 8 }}>
                                <span>Процент:</span>
                                <b style={{ color: colorVal, fontWeight: 700, fontSize: 18 }}>{percent}%</b>
                            </div>
                            <div style={{ borderTop: '1px solid #eee', margin: '8px 0 8px 0' }} />
                            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Всего за год: {total}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 15 }}>
                                {nivoKeys.map((key, idx) => {
                                    const v = info[key] || 0;
                                    const p = total > 0 ? ((v / total) * 100).toFixed(1) : '0.0';
                                    return (
                                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 1 }}>
                                            <span style={{ color: nivoColors[idx % nivoColors.length], fontWeight: 600, minWidth: 120 }}>{key}</span>
                                            <span style={{ fontWeight: 500 }}>{v} ({p}%)</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                }}
                legends={[]}
                theme={{
                    axis: {
                        legend: {
                            text: {
                                fontSize: 16,
                                fontWeight: 600
                            }
                        },
                        ticks: {
                            text: {
                                fontSize: 12,
                                fontWeight: 600
                            }
                        }
                    },
                    tooltip: {
                        container: {
                            fontSize: 14
                        }
                    }
                }}
                animate={true}
                motionConfig="gentle"
                onClick={bar => onBarClick && onBarClick(bar.data.year)}
            />
            {tooltipContent}
            <div className="bar-chart-legend" style={{ display: 'flex', gap: 24, marginTop: 18, justifyContent: 'center', flexWrap: 'wrap', position: 'relative' }}>
                {nivoKeys.map((key, idx) => {
                    const v = legendData[key] || 0;
                    const p = legendTotal > 0 ? ((v / legendTotal) * 100).toFixed(1) : '0.0';
                    return (
                        <div
                            key={key}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                fontSize: 15,
                                cursor: 'pointer',
                                color: hoveredKey === key ? nivoColors[idx % nivoColors.length] : '#555',
                                fontWeight: hoveredKey === key ? 700 : 600,
                                transition: 'color 0.2s, font-weight 0.2s',
                            }}
                            onMouseEnter={() => { console.log('hover legend', key); setHoveredKey(key); }}
                            onMouseLeave={() => { console.log('leave legend', key); setHoveredKey(null); }}
                        >
                            <span style={{
                                display: 'inline-block',
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                background: nivoColors[idx % nivoColors.length],
                                marginRight: 6,
                                border: '1.5px solid #bbb',
                            }} />
                            <span>{key} <span style={{ fontWeight: 400, color: '#888' }}>{v} ({p}%)</span></span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BarChartDynamics; 